// @flow
import React from 'react';
import { Button, ComboBoxInput } from 'bench-internal-theme';
import styled from 'styled-components';
import { toDate, formatDate } from 'date-functions';
// $FlowFixMe - TS file
import env from 'bench-environment';
import { colors } from 'bench-style-constants';

import type { AssignedPerson, Person } from './types';
import Confirm from './Confirm';

const StyledTable = styled.table`
  border-bottom: 1px solid ${colors.silt};
  border-collapse: collapse;
  margin-bottom: 92px;
  width: 100%;

  td,
  th {
    font-weight: 300;
    padding: 8px 15px;
    text-align: left;
  }

  td {
    border-top: 1px solid ${colors.silt};
    color: ${colors.charcoal};
    font-size: 14px;

    &.-action-cell {
      padding-left: 0;
      text-align: right;
      white-space: nowrap;
    }

    &.-data-cell {
      letter-spacing: -0.3px;
      white-space: nowrap;
    }

    &.-link-cell {
      text-align: center;

      a {
        color: ${colors.water};
        text-decoration: none;
      }
    }

    &.-role-select {
      min-width: 206px;
    }
  }

  th {
    background: ${colors.shell};
    color: ${colors.dust};
    font-size: 12px;
    letter-spacing: -0.2px;
    white-space: nowrap;

    &.-link-cell {
      text-align: center;
    }
  }

  td:first-child,
  th:first-child {
    padding-left: 45px;
  }

  td:last-child,
  th:last-child {
    padding-right: 45px;
  }

  tr {
    &.-notes-row td {
      border: 0 none;
      padding-bottom: 16px;
      padding-top: 4px;
    }
  }
`;

const PersonNotes = styled.div`
  background: ${colors.pearl};
  border: 1px solid ${colors.silt};
  border-radius: 3px;
  color: ${colors.charcoal};
  font-size: 14px;
  padding: 12px 10px;
  white-space: pre-wrap;
`;

/**
 * @param {string} dateString - string date in ISO8601 Format
 * @returns {string} - Date string formatted to MMMM DD, YYYY. Ex. Sept 12, 2018
 */
const getFormattedDateString = dateString => {
  if (!dateString) {
    return '';
  }

  const date = toDate(dateString);
  const dateOptions = {
    year: 'numeric',
    day: 'numeric',
    month: 'short',
  };

  return formatDate(date, dateOptions);
};

type AssignmentsTableProps = {
  assignedPersons: Array<AssignedPerson>,
  clientRoleOptions: Array<Object>,
  onRequestEditPerson: Person => void,
  onRequestRemovePerson: string => void,
  updatePersonClientRoles: (string, Array<string>) => void,
};
/**
 * @param props
 * @param props.assignedPersons - list of person objects to display
 * @param props.clientRoleOptions - the options that can be selected for clientRole
 * @param props.onRequestEditPerson - function to call when the edit button is clicked
 * @param props.onRequestRemovePerson - function to call when the edit unassign button is clicked
 * @param props.updatePersonClientRoles - function to change the client roles of a person
 */
const AssignmentsTable = ({
  assignedPersons,
  clientRoleOptions,
  onRequestEditPerson,
  onRequestRemovePerson,
  updatePersonClientRoles,
}: AssignmentsTableProps) => (
  <StyledTable>
    <thead>
      <tr>
        <th>Client Role</th>
        <th>User</th>
        <th>Company</th>
        <th>Email</th>
        <th>Phone</th>
        <th>Updated</th>
        <th>Last Login</th>
        <th className="-link-cell">Assignments</th>
        <th>{/* actions */}</th>
      </tr>
    </thead>
    <tbody>
      {assignedPersons.map(({ person, clientRoleIds, isPrincipalPerson }) => (
        <React.Fragment key={person.id}>
          <tr>
            <td className="-role-select">
              {isPrincipalPerson ? (
                'Primary Contact'
              ) : (
                <ComboBoxInput
                  isDisabled={isPrincipalPerson}
                  value={clientRoleOptions.find(
                    ({ value }) => !!clientRoleIds && String(value) === String(clientRoleIds[0]),
                  )}
                  options={clientRoleOptions}
                  onChange={selectedOption => {
                    updatePersonClientRoles(person.id, [selectedOption.value]);
                  }}
                  size="small"
                />
              )}
            </td>
            <td className="-data-cell">{person.fullName}</td>
            <td className="-data-cell">{person.company}</td>
            <td className="-data-cell">{person.email}</td>
            <td className="-data-cell">{person.phone}</td>
            <td className="-data-cell">{getFormattedDateString(person.modifiedOn)}</td>
            <td className="-data-cell">{getFormattedDateString(person.lastLoginDateTime)}</td>
            <td className="-link-cell">
              <a
                href={`${env.location.dashboard}/client-management/?ta=${person.id}`}
                rel="noopener noreferrer"
                target="_blank"
              >
                {person.assignmentCount}
              </a>
            </td>
            <td className="-action-cell">
              <Button
                icon="pencil"
                onClick={() => onRequestEditPerson(person)}
                size="small"
                styleType="ghost"
              />{' '}
              {!isPrincipalPerson && (
                <Confirm>
                  {(isConfirming, confirm) =>
                    isConfirming ? (
                      <Button
                        onClick={() => onRequestRemovePerson(person.id)}
                        size="small"
                        styleType="primary"
                      >
                        Unassign?
                      </Button>
                    ) : (
                      <Button icon="close" onClick={confirm} size="small" styleType="ghost" />
                    )
                  }
                </Confirm>
              )}
            </td>
          </tr>
          {!!person.notes && (
            <tr className="-notes-row">
              <td />
              <td colSpan="8">
                <PersonNotes>{person.notes}</PersonNotes>
              </td>
            </tr>
          )}
        </React.Fragment>
      ))}
    </tbody>
  </StyledTable>
);

export default AssignmentsTable;
